/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable no-unused-vars */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { PiBankBold } from 'react-icons/pi';
import {
  MdOutlineAccountBalanceWallet,
  MdOutlineCreditCard,
  MdOutlineQrCode2,
} from 'react-icons/md';
import { IoIosCard } from 'react-icons/io';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowRight, FiArrowLeft, FiCheck } from 'react-icons/fi';
import TravelerDetails from '../../components/TravelerDetails/TravelerDetails';
import DocumentUpload from '../../components/DocumentUpload/DocumentUpload';
import FareSummary from '../../components/FareSummary/FareSummary';
import ContactInfoCard from '../../components/ContactInfoCard/ContactInfoCard';
import LeadContactPopup from '../../components/LeadContactPopup/LeadContactPopup';
import BookingReviewModal from '../../components/BookingReviewModal/BookingReviewModal';
import {
  getRequiredDocumentsApi,
  extractVisaContext,
  performVisaBookingApi,
} from '../../api/onlineVisaApi';
import useCountries from '../../hooks/useCountries';
import {
  parseSearchQuery,
  toVisaDateString,
  formatEntry,
  formatVisaPurpose,
} from '../../utils/onlineVisaUtils';
import {
  ONLINE_VISA_ROUTES,
  VISA_PAX_TYPES,
} from '../../constants/onlineVisaConstants';
import { getUserAgentId } from '../../../../shared/helper-utils/localStorage';
import './OnlineVisaBookingPage.scss';
import AppAlert from '../../../../shared/components/app-alert/AppAlert';

function titleCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function buildInitialTravelers(search) {
  const list = [];
  const adults = Math.max(1, Number(search.adults) || 1);
  const children = Math.max(0, Number(search.children) || 0);
  const infants = Math.max(0, Number(search.infants) || 0);
  const childAges = Array.isArray(search.childAges) ? search.childAges : [];
  const infantAges = Array.isArray(search.infantAges) ? search.infantAges : [];

  for (let i = 0; i < adults; i += 1) {
    list.push({
      type: VISA_PAX_TYPES.ADULT,
      seatIndex: i + 1,
      title: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      age: '',
      passportNumber: '',
      passportIssueDate: '',
      passportExpiry: '',
      occupation: '',
      nationality: search.nationality || 'IN',
      primary: i === 0,
    });
  }
  for (let i = 0; i < children; i += 1) {
    list.push({
      type: VISA_PAX_TYPES.CHILD,
      seatIndex: i + 1,
      title: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      age: childAges[i] != null ? Number(childAges[i]) : '',
      lockedAge: childAges[i] != null ? Number(childAges[i]) : null,
      passportNumber: '',
      passportIssueDate: '',
      passportExpiry: '',
      occupation: '',
      nationality: search.nationality || 'IN',
      primary: false,
    });
  }
  for (let i = 0; i < infants; i += 1) {
    list.push({
      type: VISA_PAX_TYPES.INFANT,
      seatIndex: i + 1,
      title: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      age: infantAges[i] != null ? Number(infantAges[i]) : '',
      lockedAge: infantAges[i] != null ? Number(infantAges[i]) : null,
      passportNumber: '',
      passportIssueDate: '',
      passportExpiry: '',
      occupation: '',
      nationality: search.nationality || 'IN',
      primary: false,
    });
  }
  return list;
}

function buildInitialDocsState(documents) {
  const docs = Array.isArray(documents) ? documents : [];
  return docs.map((d, idx) => ({
    documentId: idx + 1,
    documentName: d.documentName,
    documentNumber: '',
    documentUrl: '',
    documentFormat: '',
    fileName: '',
  }));
}

function OnlineVisaBookingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { countries } = useCountries();

  const navState = location.state || {};
  const visa = navState.visa || null;
  const traceId = navState.traceId || '';

  const search = useMemo(() => {
    if (navState.search) return navState.search;
    return parseSearchQuery(searchParams);
  }, [navState.search, searchParams]);

  const paxCount = {
    adults: Number(search.adults) || 0,
    children: Number(search.children) || 0,
    infants: Number(search.infants) || 0,
  };
  const totalPax = paxCount.adults + paxCount.children + paxCount.infants;

  const destinationCode = search.destinationCode || '';
  const destinationName = useMemo(() => {
    if (search.destinationName) return search.destinationName;
    const match = countries.find((c) => c.countryCode === destinationCode);
    return match ? match.countryName.toUpperCase() : '';
  }, [countries, destinationCode, search.destinationName]);

  const citizenCountry = useMemo(() => {
    const match = countries.find((c) => c.countryCode === search.nationality);
    return match ? titleCase(match.countryName) : '';
  }, [countries, search.nationality]);

  // Travelers
  const [travelers, setTravelers] = useState(() =>
    buildInitialTravelers(search),
  );
  const handleTravelerChange = (idx, nextPax) => {
    setTravelers((prev) => {
      const copy = [...prev];
      copy[idx] = nextPax;
      return copy;
    });
  };

  // Lead contact
  const [showContactPopup, setShowContactPopup] = useState(true);
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    countryCode: '+91',
    travelDate: '',
    returnDate: '',
    confirm: false,
  });
  const handleContactSave = (values) => {
    setContactInfo(values);
    setShowContactPopup(false);
  };
  const handleContactEdit = () => setShowContactPopup(true);

  // Required documents (per-pax)
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [paxDocsState, setPaxDocsState] = useState({}); // { [seatKey]: [{...}] }
  const [alet, setALert] = useState(undefined);
  const [paymentMode, setPaymentMode] = useState('cashcard');

  useEffect(() => {
    let cancelled = false;
    if (!visa) return () => {};
    const ctx = extractVisaContext(visa, traceId);
    setDocsLoading(true);
    (async () => {
      const docs = await getRequiredDocumentsApi(ctx);
      if (cancelled) return;
      const list = Array.isArray(docs) ? docs : [];
      setRequiredDocs(list);
      // seed per-pax docs state for every traveler
      const seed = {};
      travelers.forEach((pax) => {
        const seatKey = `${pax.type}-${pax.seatIndex}`;
        seed[seatKey] = buildInitialDocsState(list);
      });
      setPaxDocsState(seed);
      setDocsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [visa, traceId]);

  const updatePaxDocs = (seatKey, nextState) => {
    setPaxDocsState((prev) => ({ ...prev, [seatKey]: nextState }));
  };

  // Review + pay
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [bookingError, setBookingError] = useState('');

  const travelersValid = useMemo(() => {
    const travelDateObj = contactInfo.travelDate
      ? new Date(contactInfo.travelDate)
      : null;
    const endTravelDate = contactInfo.returnDate
      ? new Date(contactInfo.returnDate)
      : travelDateObj;

    const allFilled = travelers.every((p) => {
      if (
        !p.title ||
        !p.firstName?.trim() ||
        !p.lastName?.trim() ||
        !p.dateOfBirth ||
        !p.passportNumber?.trim() ||
        !p.passportIssueDate ||
        !p.passportExpiry ||
        !p.occupation
      ) {
        return false;
      }

      // Passport issue date must be before travel date
      if (travelDateObj) {
        const issueDate = new Date(p.passportIssueDate);
        if (issueDate >= travelDateObj) return false;
      }

      // Passport expiry must be on or after end of trip
      if (endTravelDate) {
        const expiryDate = new Date(p.passportExpiry);
        if (expiryDate < endTravelDate) return false;
      }

      return true;
    });

    if (!allFilled) return false;
    return true;
  }, [travelers, contactInfo.travelDate, contactInfo.returnDate]);

  const passportValid = useMemo(() => {
    // Reject duplicate passport numbers across travellers
    const nums = travelers.map((p) =>
      (p.passportNumber || '').trim().toUpperCase(),
    );
    return nums.length === new Set(nums).size;
  }, [travelers]);

  const docsValid = useMemo(() => {
    if (!requiredDocs || requiredDocs.length === 0) return true;
    return travelers.every((pax) => {
      const seatKey = `${pax.type}-${pax.seatIndex}`;
      const state = paxDocsState[seatKey] || [];
      if (state.length !== requiredDocs.length) return false;
      return state.every((d) => d.documentUrl);
    });
  }, [paxDocsState, requiredDocs, travelers]);

  const contactValid = !!(
    contactInfo?.email &&
    contactInfo?.phone &&
    contactInfo?.travelDate
  );

  const handlePayClick = () => {
    setShowErrors(true);
    if (!contactValid) {
      setShowContactPopup(true);
      setALert({
        variant: 'error',
        description: 'Please complete your contact details before proceeding.',
      });
      return;
    }
    if (!travelersValid) {
      setALert({
        variant: 'error',
        description: 'Please fill all required traveller details.',
      });
      return;
    }
    if (!passportValid) {
      setALert({
        variant: 'error',
        description: 'Make sure each traveller has a unique passport number.',
      });
      return;
    }
    if (!docsValid) {
      setALert({
        variant: 'error',
        description:
          'Please upload all mandatory documents for every traveller before proceeding.',
      });
      return;
    }
    if (!termsAccepted) {
      setALert({
        variant: 'error',
        description: 'Please accept the terms and conditions to continue.',
      });
      return;
    }
    setBookingError('');
    setShowReview(true);
  };

  // Compute approx total cost for review modal (mirrors FareSummary)
  const computeTotalCost = () => {
    const total = Number(visa?.visaFare?.totalAmount) || 0;
    const adultSum = total * 1 * (paxCount.adults || 0);
    const childSum = total * 0.7 * (paxCount.children || 0);
    const infantSum = total * 0.065 * (paxCount.infants || 0);
    return Math.round(adultSum + childSum + infantSum);
  };

  const buildBookingPayload = () => {
    const ctx = extractVisaContext(visa, traceId);
    const paxList = travelers.map((p) => {
      const seatKey = `${p.type}-${p.seatIndex}`;
      const docs = (paxDocsState[seatKey] || []).map((d, i) => ({
        documentId: i + 1,
        documentName: d.documentName,
        documentNumber: `DC${d.documentId}`,
        documentUrl: d.documentUrl,
        documentFormat: d.documentFormat || 'PNG',
      }));
      return {
        title: p.title,
        firstName: p.firstName,
        lastName: p.lastName,
        paxType: p.type,
        age: Number(p.age) || 0,
        dateOfBirth: toVisaDateString(p.dateOfBirth),
        email: p.primary ? contactInfo.email : contactInfo.email,
        phoneNumber: p.primary ? contactInfo.phone : contactInfo.phone,
        countryCode: contactInfo.countryCode || '+91',
        passportNumber: p.passportNumber,
        passportIssueDate: toVisaDateString(p.passportIssueDate),
        passportExpiry: toVisaDateString(p.passportExpiry),
        nationality: p.nationality || search.nationality || 'IN',
        occupation: p.occupation,
        primary: !!p.primary,
        visaDocumentList: docs,
      };
    });

    const agentId = getUserAgentId() || '';

    return {
      bookingKey: ctx.cacheKey,
      traceId: ctx.traceId || traceId,
      cacheKey: ctx.cacheKey,
      providerId: ctx.providerId,
      visaIdToBook: visa?.visaId || '',
      clientBookingId: `TG-${Date.now()}`,
      travelDate: toVisaDateString(contactInfo.travelDate),
      returnDate: toVisaDateString(contactInfo.returnDate),
      paxList,
      visaBillingDetail: {
        add1: '',
        add2: '',
        city: '',
        state: '',
        stateTaxCode: '',
        zipcode: '',
        countryName: '',
        countryCode: '',
      },
      visaApiCredentials:
        visa?.visaApiCredentials || visa?.apiCredentials || null,
      nationality: search.nationality || 'IN',
      // eslint-disable-next-line object-shorthand
      paymentMode: paymentMode,
      travelerEmail: contactInfo.email,
      travelerContact: contactInfo.phone,
      bookedBy: agentId,
    };
  };

  const handleConfirmBooking = async () => {
    setSubmitting(true);
    setBookingError('');
    try {
      const payload = buildBookingPayload();
      const result = await performVisaBookingApi(payload);
      if (result && result.ok) {
        if (paymentMode === 'cashcard') {
          setShowReview(false);
          navigate(
            ONLINE_VISA_ROUTES.BOOKING_DETAILS.replace(
              ':transactionId',
              encodeURIComponent(result.transactionId),
            ),
            { state: { result, visa, travelers, contactInfo } },
          );
        } else {
          const { response } = result;
          const apiResult = response?.ApiStatus.Result || {};
          if (apiResult.PgUrl && response?.ApiStatus?.TransactionStatus) {
            window.location.href = `${apiResult.PgUrl}?txid=${apiResult.Txid}`;
          } else {
            setALert({
              variant: 'error',
              description:
                result?.message ||
                'Failed to book, please contact customer care',
            });
          }
        }
      } else {
        setALert({
          variant: 'error',
          description:
            result.message || 'Failed to book, please contact customer care',
        });
      }
    } catch (e) {
      setBookingError('Booking failed. Please review and retry.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToOptions = () => {
    navigate(-1);
  };

  const totalCost = computeTotalCost();

  return (
    <main className="online-visa-booking">
      {alet &&
        (!contactValid ||
          !travelersValid ||
          !docsValid ||
          !termsAccepted ||
          !passportValid) && (
          <AppAlert
            showIcon
            variant={alet.variant}
            description={alet.description}
            onHide={() => {
              setALert(undefined);
            }}
          />
        )}
      <Container>
        {/* BLACK SUMMARY BAR (reused from options page) */}
        <section className="visa-summary-bar">
          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">VISA FOR CITIZEN OF</span>
            <strong className="visa-summary-bar__value">
              {search.nationality || '—'}
            </strong>
            <span className="visa-summary-bar__sub">{citizenCountry}</span>
          </div>

          <button
            type="button"
            className="visa-summary-bar__arrow"
            aria-label="Visa flow"
            tabIndex={-1}
          >
            <FiArrowRight />
          </button>

          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">DESTINATION COUNTRY</span>
            <strong className="visa-summary-bar__value">
              {destinationCode || '—'}
            </strong>
            <span className="visa-summary-bar__sub">
              {titleCase(destinationName)}
            </span>
          </div>

          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">VISA TYPE</span>
            <strong className="visa-summary-bar__value">
              {(visa?.visaType || formatVisaPurpose(search.visaPurpose))
                .toString()
                .toUpperCase()}
            </strong>
            {visa?.entry && (
              <span className="visa-summary-bar__sub">
                {formatEntry(visa.entry)}
              </span>
            )}
          </div>

          <div className="visa-summary-bar__segment">
            <span className="visa-summary-bar__label">TRAVELLERS</span>
            <strong className="visa-summary-bar__value">
              {totalPax} {totalPax === 1 ? 'Traveller' : 'Travellers'}
            </strong>
          </div>
        </section>

        <Row className="g-4 online-visa-booking__main">
          {/* LEFT: traveler form + docs */}
          <Col lg={9} md={12}>
            <div className="online-visa-booking__panel">
              <TravelerDetails
                travelers={travelers}
                onChange={handleTravelerChange}
                showErrors={showErrors}
                travelDate={contactInfo.travelDate}
                returnDate={contactInfo.returnDate}
              />

              <div className="online-visa-booking__docs-wrap">
                {docsLoading && (
                  <div className="online-visa-booking__docs-loader">
                    Loading required documents…
                  </div>
                )}
                {!docsLoading && requiredDocs.length === 0 && (
                  <div className="online-visa-booking__docs-empty">
                    No documents are required for this visa.
                  </div>
                )}
                {!docsLoading &&
                  requiredDocs.length > 0 &&
                  travelers.map((pax) => {
                    const seatKey = `${pax.type}-${pax.seatIndex}`;
                    const paxLabel =
                      [pax.title, pax.firstName, pax.lastName]
                        .filter(Boolean)
                        .join(' ') || `${pax.type} ${pax.seatIndex}`;
                    return (
                      <DocumentUpload
                        key={seatKey}
                        documents={requiredDocs}
                        paxLabel={paxLabel}
                        docsState={paxDocsState[seatKey] || []}
                        onDocsChange={(next) => updatePaxDocs(seatKey, next)}
                        showErrors={showErrors}
                      />
                    );
                  })}
                <div className="online-visa-booking__payment">
                  <h5 className="online-visa-booking__payment-title">
                    Payment Method
                  </h5>
                  <div className="online-visa-booking__payment-modes">
                    {[
                      {
                        key: 'cashcard',
                        icon: <MdOutlineAccountBalanceWallet />,
                        label: 'Wallet',
                      },
                      {
                        key: 'creditcard',
                        icon: <MdOutlineCreditCard />,
                        label: 'Credit Card',
                      },
                      {
                        key: 'debitcard',
                        icon: <IoIosCard />,
                        label: 'Debit Card',
                      },
                      {
                        key: 'netbanking',
                        icon: <PiBankBold />,
                        label: 'Net Banking',
                      },
                      { key: 'upi', icon: <MdOutlineQrCode2 />, label: 'UPI' },
                    ].map(({ key, icon, label }) => (
                      <div
                        key={key}
                        role="button"
                        tabIndex={0}
                        className={`online-visa-booking__pay-mode-btn${
                          paymentMode === key ? ' active' : ''
                        }`}
                        onClick={() => setPaymentMode(key)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setPaymentMode(key);
                          }
                        }}
                      >
                        <span className="online-visa-booking__pay-mode-icon">
                          {icon}
                        </span>
                        <span className="online-visa-booking__pay-mode-label">
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Col>

          {/* RIGHT: sidebar */}
          <Col lg={3} md={12}>
            <div className="online-visa-booking__sidebar">
              <FareSummary
                visa={visa}
                paxCount={paxCount}
                childAges={search.childAges}
                infantAges={search.infantAges}
              />
              <ContactInfoCard info={contactInfo} onEdit={handleContactEdit} />
            </div>
          </Col>
        </Row>
      </Container>

      {/* STICKY BOTTOM BAR */}
      <div className="online-visa-booking__bottom-bar">
        <Container className="online-visa-booking__bottom-inner">
          <label className="online-visa-booking__terms">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
            />
            <span>
              I confirm the details and agree to the{' '}
              <a
                href="https://agents.fastays.com/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                terms and conditions
              </a>
              .
            </span>
          </label>
          {/* {showErrors && !termsAccepted && (
            <span className="online-visa-booking__terms-err">
              Please accept the terms to continue.
            </span>
          )} */}
          <div className="online-visa-booking__bottom-actions">
            <button
              type="button"
              className="online-visa-booking__back-btn"
              onClick={handleBackToOptions}
            >
              <FiArrowLeft />
              <span>Back</span>
            </button>
            <button
              type="button"
              className="online-visa-booking__pay-btn"
              onClick={handlePayClick}
              disabled={submitting}
            >
              <FiCheck />
              <span>Pay Now</span>
            </button>
          </div>
        </Container>
      </div>

      {/* LEAD CONTACT POPUP */}
      <LeadContactPopup
        minTravelDate={visa?.minTravelDays}
        show={showContactPopup}
        onHide={() => setShowContactPopup(false)}
        initialValues={contactInfo}
        onContinue={handleContactSave}
        stayDuration={visa?.duration}
      />

      {/* REVIEW & CONFIRM MODAL */}
      <BookingReviewModal
        show={showReview}
        onHide={() => setShowReview(false)}
        onConfirm={handleConfirmBooking}
        visa={visa}
        travelers={travelers}
        contactInfo={contactInfo}
        submitting={submitting}
        totalCost={totalCost}
        currency={visa?.visaFare?.currency || 'INR'}
      />

      {bookingError && (
        <div className="online-visa-booking__toast">{bookingError}</div>
      )}
    </main>
  );
}

export default OnlineVisaBookingPage;
